import { Link, useRouterState } from '@tanstack/react-router';
import { Fragment, type ReactNode } from 'react';
import { iconForPath } from './app-sidebar';
import { PageBarContent } from './page-bar';

export interface Crumb {
  label: string;
  to: string;
}

/**
 * Puts the page's icon, breadcrumb, title and actions in the bar above the page; the description
 * stays at the top of the page as quiet supporting text.
 */
export function PageHeader({
  title,
  description,
  actions,
  breadcrumb = [],
  titleAddon,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  /** Parent pages, outermost first (the current page is `title`). */
  breadcrumb?: Crumb[];
  /** Shown right after the title, e.g. a status badge or a switcher. */
  titleAddon?: ReactNode;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const Icon = iconForPath(pathname);
  return (
    <>
      <PageBarContent>
        <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2 text-sm">
          {Icon && <Icon className="text-muted-foreground size-4 shrink-0" aria-hidden />}
          {breadcrumb.map((c) => (
            <Fragment key={c.to}>
              <Link
                // The crumb paths are plain strings from the page; the router checks them at runtime.
                to={c.to as '/'}
                className="text-muted-foreground hover:text-foreground hidden shrink-0 font-medium sm:inline"
              >
                {c.label}
              </Link>
              <span className="text-muted-foreground/60 hidden sm:inline" aria-hidden>
                /
              </span>
            </Fragment>
          ))}
          <h1 className="min-w-0 truncate font-medium">{title}</h1>
          {titleAddon}
        </nav>
        {actions && <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div>}
      </PageBarContent>
      {description && <p className="text-muted-foreground mb-4 max-w-3xl text-sm">{description}</p>}
    </>
  );
}
